/**
 * Marker dictionaries of the screening engine.
 *
 * Every marker is a regular expression matched against folded text (lowercase, no
 * diacritics). A marker contributes its weight at most once per category, so the score
 * reflects which signals are present, not how often a word is repeated.
 * Tune the engine by editing weights here — every change is covered by scenario tests.
 */
import { Category } from "./types";

export interface Marker {
    re: RegExp;
    weight: number;
    /** Czech label shown in the screening detail. */
    label: string;
}

/** Keywords used to assign sentences to a category when the document has no headings. */
export const CATEGORY_KEYWORDS: Record<Category, RegExp> = {
    vision: /\b(viz[ei]|strategi|dlouhodob|cil(em|e|y|u)?\b|expanz|inovac|trzni podil|konkurencni)/,
    finance: /\b(financ|rozpocet|rozpoctu|obrat|trzb|zisk(u|em|ovost)?\b|ztrat|ebitda|roi\b|navratnost|kapital|aktiva|zavazk|uver|cash ?flow|capex|opex|naklad|vysledek hospodareni|kc\b)/,
    implementation: /\b(implementac|realizac|nasazeni|harmonogram|faz[ei]|etap|milnik|pilot|poc\b|proof of concept|instalac|integrac|skoleni|testovani|migrac)/,
    risks: /\b(rizik|hrozb|insolvenc|exekuc|spor|zalob|pokut|sankc|zavislost|zpozdeni|bezpecnost|pravdepodobnost)/,
    operations: /\b(provoz|udrzb|monitor|dashboard|sla\b|podpor|servis|efektiv|uspor|proces|logistik|real(nem|ny) cas|24\/7)/,
};

/** Section heading words, e.g. "2. Vize a strategické cíle", "Finanční rozbor" (folded). */
export const HEADING_WORDS: Record<Category, RegExp> = {
    vision: /(vize|strategie|strategicke cile)\b/,
    finance: /(financni (rozbor|udaje|analyza|situace|zdravi)|ekonomick\w* (udaje|situace)|rozpocet projektu)\b/,
    implementation: /(technicka implementace|implementace|realizace( projektu)?|harmonogram)\b/,
    risks: /(analyza rizik|rizika|identifikovana rizika)\b/,
    operations: /(provozni (aspekty|efektivita|udaje)|provoz)\b/,
};

export const MARKERS: Record<Category, Marker[]> = {
    vision: [
        { re: /\bviz[ei]\b|\bvizi\b/, weight: 6, label: "definovaná vize" },
        { re: /strategi/, weight: 6, label: "strategie" },
        { re: /dlouhodob/, weight: 4, label: "dlouhodobý horizont" },
        { re: /\bcil(em|e|y|u)?\b/, weight: 4, label: "stanovené cíle" },
        { re: /expanz|novych trh|zahranic/, weight: 5, label: "expanze" },
        { re: /inovac|vyvoj novych/, weight: 5, label: "inovace" },
        { re: /automatizac|digitalizac|robot/, weight: 5, label: "automatizace / digitalizace" },
        { re: /trzni podil|konkurencni vyhod/, weight: 5, label: "tržní pozice" },
        { re: /bez (jasne )?strategie|strategie (neni|chybi)|nejasn\w* (vize|smer)/, weight: -12, label: "chybí strategie" },
        { re: /stagnac|utlum cinnosti|omezeni cinnosti/, weight: -10, label: "stagnace / útlum" },
    ],
    finance: [
        { re: /rozpocet|rozpoctu/, weight: 4, label: "stanovený rozpočet" },
        { re: /\broi\b|navratnost/, weight: 6, label: "vyčíslená návratnost" },
        // "zisk" must not match "získat" (folded to "ziskat")
        { re: /\bzisk(u|em|ovost\w*|ov[aeyi])?\b|kladn\w* vysledek hospodareni/, weight: 8, label: "zisk" },
        { re: /ebitda/, weight: 5, label: "EBITDA" },
        { re: /cash ?flow|penezni tok/, weight: 4, label: "cash flow" },
        { re: /vlastni kapital/, weight: 3, label: "vlastní kapitál" },
        { re: /rust (trzeb|obratu|trzby)|trzby vzrostly|obrat vzrostl/, weight: 8, label: "růst tržeb" },
        { re: /bez (bankovnich )?(uveru|dluhu)|nulov\w* zadluzeni/, weight: 6, label: "bez zadlužení" },
        { re: /\bcapex\b|\bopex\b|provozni naklady/, weight: 3, label: "rozpad nákladů" },
        { re: /\bztrat[auy]\b|zaporn\w* vysledek|ztratov/, weight: -12, label: "ztráta" },
        { re: /pokles (trzeb|obratu)|trzby klesly|obrat klesl/, weight: -10, label: "pokles tržeb" },
        { re: /po splatnosti|druhotna platebni neschopnost/, weight: -14, label: "závazky po splatnosti" },
        { re: /predluzen|zaporn\w* vlastni kapital/, weight: -18, label: "předlužení" },
        { re: /\buver|\bpujck|\bleasing/, weight: -2, label: "úvěrové financování" },
    ],
    implementation: [
        { re: /\bfaz[eiy]|\betap/, weight: 5, label: "fázování" },
        { re: /harmonogram|casovy plan/, weight: 6, label: "harmonogram" },
        { re: /milnik/, weight: 5, label: "milníky" },
        { re: /\bpilot|\bpoc\b|proof of concept/, weight: 5, label: "pilot / PoC" },
        { re: /\baudit/, weight: 3, label: "vstupní audit" },
        { re: /skoleni/, weight: 4, label: "školení" },
        { re: /testovani|\buat\b|kalibrac/, weight: 4, label: "testování" },
        { re: /nasazeni|instalac/, weight: 3, label: "plán nasazení" },
        { re: /integrac|\bapi\b/, weight: 3, label: "integrace" },
        { re: /projektov\w* (manazer|rizeni)|\bscrum\b|agilni/, weight: 4, label: "projektové řízení" },
        { re: /bez harmonogramu|termin\w* (neni|nejsou) (stanoven|urcen)|neurcen\w* termin/, weight: -10, label: "chybí harmonogram" },
        { re: /nedostatek (kapacit|specialist|lidi|personalu)|chybi kapacit/, weight: -8, label: "nedostatek kapacit" },
    ],
    risks: [
        { re: /vysok\w* rizik/, weight: -10, label: "vysoké riziko" },
        { re: /kritick\w* rizik/, weight: -12, label: "kritické riziko" },
        { re: /insolvenc/, weight: -25, label: "insolvence" },
        { re: /exekuc/, weight: -20, label: "exekuce" },
        { re: /soudni spor|\bzalob/, weight: -10, label: "soudní spor" },
        { re: /\bpokut|\bsankc/, weight: -8, label: "pokuty / sankce" },
        { re: /zavislost na (jedno|jedin)\w* (zakazni|odberatel|dodavatel)/, weight: -10, label: "závislost na jednom partnerovi" },
        { re: /zpozdeni/, weight: -4, label: "riziko zpoždění" },
        { re: /kyberneti|kyberutok/, weight: -3, label: "kybernetické riziko" },
        { re: /fluktuac|odpor zamestnanc/, weight: -4, label: "lidský faktor" },
        { re: /kurzov/, weight: -3, label: "kurzové riziko" },
        { re: /\bresen[oa]\b|\bzmirn|mitigac|\bopatreni/, weight: 8, label: "navržená opatření" },
        { re: /pojisten/, weight: 6, label: "pojištění" },
        { re: /sifrovan|\baes\b|zalohovan|\bbackup|redundan/, weight: 6, label: "technické zabezpečení" },
        { re: /\biso\s?\d{4,5}|certifik/, weight: 6, label: "certifikace" },
        { re: /nizk\w* (rizik|pravdepodobnost)/, weight: 6, label: "nízké riziko" },
        { re: /analyza rizik|registr rizik/, weight: 4, label: "řízení rizik" },
    ],
    operations: [
        { re: /udrzb/, weight: 3, label: "plán údržby" },
        { re: /monitor/, weight: 5, label: "monitoring" },
        { re: /dashboard|reporting/, weight: 4, label: "reporting" },
        { re: /real(nem|ny) cas|real-?time/, weight: 5, label: "data v reálném čase" },
        { re: /\bsla\b/, weight: 5, label: "SLA" },
        { re: /24\/7|nepretrzit/, weight: 5, label: "nepřetržitá podpora" },
        { re: /\bpodpor[auy]\b|\bservis/, weight: 3, label: "podpora / servis" },
        { re: /efektivit|efektivn/, weight: 4, label: "důraz na efektivitu" },
        { re: /uspor/, weight: 5, label: "úspory" },
        { re: /eliminuj|snizuje (naklad|chyb)|snizeni (naklad|chyb)/, weight: 5, label: "snižování nákladů / chyb" },
        { re: /minimaln\w* udrzb/, weight: 5, label: "nízká náročnost provozu" },
        { re: /vypadk|\bprostoj/, weight: -6, label: "výpadky provozu" },
        { re: /neefektiv/, weight: -8, label: "neefektivní procesy" },
        { re: /nedostatek (personalu|kapacit|pracovnik)/, weight: -6, label: "nedostatek personálu" },
    ],
};

/** Findings that are always reported as red flags in the summary. */
export const RED_FLAGS: Marker[] = [
    { re: /insolvenc/, weight: 0, label: "insolvenční řízení" },
    { re: /exekuc/, weight: 0, label: "exekuce" },
    { re: /predluzen|zaporn\w* vlastni kapital/, weight: 0, label: "předlužení" },
    { re: /po splatnosti|druhotna platebni neschopnost/, weight: 0, label: "závazky po splatnosti" },
    { re: /soudni spor|\bzalob/, weight: 0, label: "soudní spor" },
];
